import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import LinearProgress from "@mui/material/LinearProgress";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import {
  FormGroup,
  FormControlLabel,
  Switch,
  TextField,
  MenuItem,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Typography,
} from "@mui/material";
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
  getPickupSettings,
  postPickupSettings,
  resetPickupSettingsState,
} from "./pickupSettingsSlice";
import { fetchCountry, fetchState } from '../../../pages/General/countryStateCitySlice';
import Notification from '../../../pages/General/Notification';

// Global enterprise styling dictionary configuring standard outlined layout inputs
const enterprisePickupInputStyles = {
    width: '100%',
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        backgroundColor: '#FFFFFF',
        transition: 'all 0.2s ease-in-out',
        border: '1px solid #E2E8F0',
        '& fieldset': { border: 'none' },
        '&:hover': {
            borderColor: '#CBD5E1',
            boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
        },
        '&.Mui-focused': {
            borderColor: '#1976d2', // --- CORPORATE IDENTITY BRAND BLUE ---
            boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.08)'
        },
        '&.Mui-disabled': {
            backgroundColor: '#F8FAFC',
            borderColor: '#F1F5F9',
            color: '#94A3B8'
        }
    },
    '& .MuiInputLabel-outlined': {
        color: '#64748B',
        fontWeight: 500,
        transform: 'translate(14px, 10px) scale(1)',
        '&.Mui-focused, &.MuiInputLabel-shrink': {
            transform: 'translate(14px, -8px) scale(0.75)',
            color: '#1976d2',
            fontWeight: 600
        },
        '&.Mui-disabled': {
            color: '#CBD5E1'
        }
    },
    '& input, & .MuiSelect-select': {
        padding: '10px 14px !important',
        fontSize: '13.5px',
        color: '#1E293B',
        fontWeight: 500
    },
    '& .MuiFormHelperText-root': {
        mx: 0.5,
        mt: 0.75,
        fontWeight: 500
    }
};

const PickupSettingsPage = () => {
  const dispatch = useDispatch();
  const [alert, setAlert] = useState(false);
  const [open, setOpen] = useState(false);
  const pickupsettings = useSelector((state) => state.pickupsettings);
  const profile = useSelector(state => state.auth.profile);    
  const country = useSelector(state => state.countrystate.country);
  const state = useSelector(state => state.countrystate.state);
  const timeOptions = Array.from({ length: 24 }, (_, i) => `${i.toString().padStart(2, '0')}:00`);
  const initialValues = {
        pickup: false,
        name: '',
        address: '',
        address2: '',
        city: '',
        company: '',
        country_code: '',
        email: '',
        phone: '',
        postal_code: '',
        state_code: '',
        start_time:'',
        end_time:''
    }

  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const onLabelChange = (e) => {
    const { name, value } = e.target;
    setValues({
      ...values,
      [name]: e.target.checked,
    });
  };

  const handleInputChange = e => {
        const { name, value } = e.target
        if(e.target.name === 'postal_code'){
            setValues({
                ...values,
                [name]: value.replace(/\s/g, ''),
            });
       }else{
            setValues({
                ...values,
                [name]: value
            })
        }
    }

  const validateFields = () => {
  const newErrors = {};

  if (!values.pickup) return newErrors; // Skip validation if pickup is false

  // Required text fields
  const requiredFields = [
    'name',
    'company',
    'phone',
    'email',
    'address',
    'postal_code',
    'city',
    'country_code',
    'state_code',
    'start_time',
    'end_time',
  ];

  requiredFields.forEach((field) => {
    if (!values[field]) {
      newErrors[field] = 'This field is required';
    }
  });

  // Email format
  if (values.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
    newErrors.email = 'Invalid email format';
  }

  // Phone format (basic numeric check)
  if (values.phone && !/^\d{6,20}$/.test(values.phone)) {
    newErrors.phone = 'Invalid phone number';
  }

  // Time logic
  const start = dayjs(values.start_time, 'HH:mm');
  const end = dayjs(values.end_time, 'HH:mm');
  if (start.isValid() && end.isValid() && !start.isBefore(end)) {
    newErrors.start_time = 'Start time must be earlier than end time';
    newErrors.end_time = 'End time must be later than start time';
  }

  return newErrors;
};

const handleFieldBlur = (e) => {
  const { name, value } = e.target;
  const newErrors = { ...errors };

  // Basic required field check
  if (!value.trim()) {
    newErrors[name] = 'This field is required';
  } else {
    delete newErrors[name];

    // Optional: add format checks
    if (name === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      newErrors[name] = 'Invalid email format';
    }

    if (name === 'phone' && !/^\d{6,20}$/.test(value)) {
      newErrors[name] = 'Invalid phone number';
    }
  }

  setErrors(newErrors);
};

const handleSubmit = (e) => {
  e.preventDefault();

  const validationErrors = validateFields();
  setErrors(validationErrors);

  if (Object.keys(validationErrors).length === 0) {
    const finalData = {
      is_enabled: values.pickup,
      address: {
        name: values.name,
        company: values.company,
        phone: values.phone,
        email: values.email,
        address: values.address,
        address2: values.address2,
        postal_code: values.postal_code,
        city: values.city,
        country_code: values.country_code,
        state_code: values.state_code,
      },
      start_time: values.start_time,
      end_time: values.end_time,
    };

    dispatch(postPickupSettings(profile && profile.id,finalData));
  }
};

useEffect(() => {
  const config = pickupsettings?.data;

  if (config && config.address) {
    dispatch(fetchState(config.address.country_code));
    setValues({
      pickup: config.is_enabled || false,
      start_time: config.start_time || '',
      end_time: config.end_time || '',
      name: config.address.name || '',
      company: config.address.company || '',
      phone: config.address.phone || '',
      email: config.address.email || '',
      address: config.address.address || '',
      address2: config.address.address2 || '',
      postal_code: config.address.postal_code || '',
      city: config.address.city || '',
      country_code: config.address.country_code || '',
      state_code: config.address.state_code || '',
    });
  }
}, [pickupsettings]);

  const handleClose = () => {
    setOpen(false);
    dispatch(resetPickupSettingsState());
  };

  useEffect(() => {
    dispatch(getPickupSettings());
  }, []);

   useEffect(() => {
      dispatch(fetchCountry());
   }, [dispatch]);
  const filteredCountryData = country.data.filter(({ code }) => code );
   
  const onSelectCountryChange = (countryCode) => {
      setValues({
        ...values,
        country_code: countryCode,
      });
      setErrors({ country_code: "" });
      dispatch(fetchState(countryCode));
    };
  const getSelectedCountry = (country_code) => {
      let filteredValue = { code: "", name: "" };
      filteredCountryData.map((key) => {
        if (key.code == country_code) {
          filteredValue = key;
        }
      });
      return filteredValue;
    };
  
    const onSelectStateChange = (currentstate) => {
      setValues({
        ...values,
        state_code: currentstate,
      });
      setErrors({ state_code: "" });
    };
  
    const getSelectedState = (currentstate) => {
      let filteredStateValue = { code: "", name: "" };
      let tempState = "";
      state &&
        state.data &&
        state.data.map((key) => {
          if (key.code == currentstate && tempState == "") {
            filteredStateValue = key;
            tempState = "success";
          }
        });
      return filteredStateValue;
    };

  return (
    <Box className="sc--settings--card" sx={{ width: '100%', boxSizing: 'border-box', p: 2.5 }}>
      {(pickupsettings && pickupsettings.notification && pickupsettings.notification.messageContent!='') &&
            <Notification
                is_open={pickupsettings.notification.show}
                messageType={pickupsettings.notification.messageType}
                messages={pickupsettings.notification.messageContent}
                handleClose={() => dispatch(resetPickupSettingsState())}
       />}
      <Card
        sx={{ 
            width: '100%', 
            backgroundColor: '#FFFFFF',
            border: '1px solid #E2E8F0',
            borderRadius: '16px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.02)',
            overflow: 'hidden'
        }}
        className="sc--cardhead--details"
      >
        <CardHeader 
            title={
                <Typography variant="h6" component="h1" sx={{ fontWeight: 800, color: '#0F172A', fontSize: '16px', m: 0 }}>
                    Pickup Settings
                </Typography>
            } 
            sx={{ p: 2.5, backgroundColor: '#FFFFFF' }}
        />
        
        {pickupsettings && pickupsettings.loading === true && (
          <LinearProgress sx={{ height: '3px', backgroundColor: '#E2E8F0', '& .MuiLinearProgress-bar': { backgroundColor: '#1976d2' } }} />
        )}
        
        <Divider sx={{ borderColor: '#E2E8F0' }} />
        
        {pickupsettings && pickupsettings.loading === false && (
          <CardContent className="sc--label--settings" sx={{ p: 4, boxSizing: 'border-box' }}>
            <Grid container spacing={3}>
              
              {/* Toggle Option Control Module Bar */}
              <Grid item xs={12}>
                <Box 
                  className="sc--form--center" 
                  sx={{ 
                    display: 'flex', 
                    justifyContent: 'flex-start',
                    backgroundColor: '#F8FAFC',
                    p: '10px 20px',
                    borderRadius: '12px',
                    border: '1px dashed #E2E8F0'
                  }}
                >
                  <FormGroup className="sc--label--settings--label">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.pickup}
                          name="pickup"
                          onChange={onLabelChange}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': { color: '#1976d2' },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#1976d2' }
                          }}
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#334155', mr: 2 }}>
                            Enable Pickup Service
                        </Typography>
                      }
                      labelPlacement="start"
                    />
                  </FormGroup>
                </Box>
              </Grid>

              {/* 1. Name Input Field */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ minHeight: '40px' }}>
                  <TextField
                    label="Name*"
                    name="name"
                    type="text"
                    size="small"
                    value={values.name}
                    onChange={handleInputChange}
                    inputProps={{ maxLength: 40 }}
                    error={!!errors.name}
                    helperText={errors.name}
                    onBlur={handleFieldBlur}
                    variant="outlined"
                    disabled={!values.pickup}
                    sx={enterprisePickupInputStyles}
                  />
                </Box>
              </Grid>

              {/* 2. Corporate Title Field */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ minHeight: '40px' }}>
                  <TextField
                    label="Company*"
                    name="company"
                    type="text"
                    size="small"
                    value={values.company}
                    onChange={handleInputChange}
                    inputProps={{ maxLength: 40 }}
                    error={!!errors.company}
                    helperText={errors.company}
                    onBlur={handleFieldBlur}
                    variant="outlined"
                    disabled={!values.pickup}
                    sx={enterprisePickupInputStyles}
                  />
                </Box>
              </Grid>

              {/* 3. Telecom Coordinates Field */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ minHeight: '40px' }}>
                  <TextField
                    label="Phone*"
                    name="phone"
                    size="small"
                    value={values.phone}
                    onChange={handleInputChange}
                    inputProps={{ maxLength: 20 }}
                    error={!!errors.phone}
                    helperText={errors.phone}
                    onBlur={handleFieldBlur}
                    variant="outlined"
                    disabled={!values.pickup}
                    sx={enterprisePickupInputStyles}
                  />
                </Box>
              </Grid>

              {/* 4. Electronic Mail Coordinates Field */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ minHeight: '40px' }}>
                  <TextField
                    label="Email*"
                    name="email"
                    type="email"
                    size="small"
                    value={values.email}
                    inputProps={{ maxLength: 60 }}
                    onChange={handleInputChange}
                    error={!!errors.email}
                    helperText={errors.email}
                    onBlur={handleFieldBlur}
                    variant="outlined"
                    disabled={!values.pickup}
                    sx={enterprisePickupInputStyles}
                  />
                </Box>
              </Grid>

              {/* 5. Primary Street Destination Line Field */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ minHeight: '40px' }}>
                  <TextField
                    label="Address1*"
                    name="address"
                    size="small"
                    value={values.address}
                    inputProps={{ maxLength: 35 }}
                    onChange={handleInputChange}
                    error={!!errors.address}
                    helperText={errors.address}
                    onBlur={handleFieldBlur}
                    variant="outlined"
                    disabled={!values.pickup}
                    sx={enterprisePickupInputStyles}
                  />
                </Box>
              </Grid>

              {/* 6. Secondary Suite Navigation Line Field */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ minHeight: '40px' }}>
                  <TextField
                    label="Address2"
                    name="address2"
                    size="small"
                    value={values.address2}
                    inputProps={{ maxLength: 35 }}
                    onChange={handleInputChange}
                    error={!!errors.address2}
                    helperText={errors.address2}
                    onBlur={handleFieldBlur}
                    variant="outlined"
                    disabled={!values.pickup}
                    sx={enterprisePickupInputStyles}
                  />
                </Box>
              </Grid>

              {/* 7. Postal Code Localization Identifier Field */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ minHeight: '40px' }}>
                  <TextField
                    label="Postal Code*"
                    name="postal_code"
                    size="small"
                    value={values.postal_code}
                    onChange={handleInputChange}
                    inputProps={{ maxLength: 10 }}
                    error={!!errors.postal_code}
                    helperText={errors.postal_code}
                    onBlur={handleFieldBlur}
                    variant="outlined"
                    disabled={!values.pickup}
                    sx={enterprisePickupInputStyles}
                  />
                </Box>
              </Grid>

              {/* 8. Regional Municipality Core Identifier Field */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ minHeight: '40px' }}>
                  <TextField
                    label="City*"
                    name="city"
                    size="small"
                    value={values.city}
                    inputProps={{ maxLength: 30 }}
                    onChange={handleInputChange}
                    error={!!errors.city}
                    helperText={errors.city}
                    onBlur={handleFieldBlur}
                    variant="outlined"
                    disabled={!values.pickup}
                    sx={enterprisePickupInputStyles}
                  />
                </Box>
              </Grid>

              {/* 9. Sovereign Territory Country Autocomplete Field */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ minHeight: '40px' }}>
                  <Autocomplete
                    className="help-text-error"
                    disablePortal
                    size="small"
                    name="country_code"
                    id="country-select-demo"
                    options={filteredCountryData}
                    autoHighlight
                    loading={country && country.loading}
                    getOptionLabel={(option) => option.name || ""}
                    value={getSelectedCountry(values.country_code)}
                    disabled={!values.pickup}
                    sx={{
                      width: '100%',
                      '& .MuiAutocomplete-inputRoot': enterprisePickupInputStyles['& .MuiOutlinedInput-root'],
                      '& .MuiInputLabel-root': enterprisePickupInputStyles['& .MuiInputLabel-outlined'],
                      '& input': enterprisePickupInputStyles['& input']
                    }}
                    renderOption={(props, option) => (
                      <MenuItem {...props} value={option.code} key={option.code} sx={{ fontSize: '13.5px', fontWeight: 500, color: '#1E293B' }}>
                        {option.icon} {option.name}
                      </MenuItem>
                    )}
                    onChange={(event, value) => {
                      if (value !== null) {
                        onSelectCountryChange(value.code);
                        setErrors({ country_code: "" });
                      } else {
                        onSelectCountryChange("");
                        setErrors({ country_code: "This field is required" });
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Country*"
                        variant="outlined"
                        error={!!errors.country_code}
                        helperText={errors.country_code}
                        onBlur={handleFieldBlur}
                        disabled={!values.pickup}
                        sx={{ '& .MuiFormHelperText-root': enterprisePickupInputStyles['& .MuiFormHelperText-root'] }}
                      />
                    )}
                  />
                </Box>
              </Grid>

              {/* 10. Secondary Administrative State Autocomplete Field */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ minHeight: '40px' }}>
                  <Autocomplete
                    className="help-text-error"
                    disablePortal
                    size="small"
                    name="state_code"
                    id="state-select-demo"
                    options={state?.data || []}
                    autoHighlight
                    loading={state?.loading || country?.loading}
                    getOptionLabel={(option) => option.name || ""}
                    value={getSelectedState(values.state_code)}
                    disabled={!values.pickup}
                    sx={{
                      width: '100%',
                      '& .MuiAutocomplete-inputRoot': enterprisePickupInputStyles['& .MuiOutlinedInput-root'],
                      '& .MuiInputLabel-root': enterprisePickupInputStyles['& .MuiInputLabel-outlined'],
                      '& input': enterprisePickupInputStyles['& input']
                    }}
                    renderOption={(props, option) => (
                      <MenuItem {...props} value={option.code} key={option.code} sx={{ fontSize: '13.5px', fontWeight: 500, color: '#1E293B' }}>
                        {option.name}
                      </MenuItem>
                    )}
                    onChange={(event, value) => {
                      if (value !== null) {
                        onSelectStateChange(value.code);
                      } else {
                        onSelectStateChange("");
                        setErrors({ state_code: "This field is required" });
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="State*"
                        variant="outlined"
                        error={!!errors.state_code}
                        helperText={errors.state_code}
                        onBlur={handleFieldBlur}
                        disabled={!values.pickup}
                        sx={{ '& .MuiFormHelperText-root': enterprisePickupInputStyles['& .MuiFormHelperText-root'] }}
                      />
                    )}
                  />
                </Box>
              </Grid>

              {/* 11. Start Time Picker Module Field */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ minHeight: '40px' }}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <FormControl variant="outlined" size="small" sx={{ width: '100%' }} disabled={!values.pickup}>
                      <TimePicker
                        label="Start Time*"
                        value={values.start_time ? dayjs(values.start_time, 'HH:mm') : null}
                        disabled={!values.pickup}
                        onChange={(newValue) => {
                          const formatted = newValue ? newValue.format('HH:mm') : '';
                          handleInputChange({ target: { name: 'start_time', value: formatted } });

                          if (formatted) {
                            setErrors((prev) => {
                              const updated = { ...prev };
                              delete updated.start_time;
                              return updated;
                            });
                          }
                        }}
                        ampm={false}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            size="small"
                            error={!!errors.start_time}
                            helperText={errors.start_time}
                            disabled={!values.pickup}
                            sx={enterprisePickupInputStyles}
                          />
                        )}
                      />
                    </FormControl>
                  </LocalizationProvider>
                </Box>
              </Grid>

              {/* 12. End Time Picker Module Field */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ minHeight: '40px' }}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <FormControl variant="outlined" size="small" sx={{ width: '100%' }} disabled={!values.pickup}>
                      <TimePicker
                        label="End Time*"
                        value={values.end_time ? dayjs(values.end_time, 'HH:mm') : null}
                        disabled={!values.pickup}
                        onChange={(newValue) => {
                          const formatted = newValue ? newValue.format('HH:mm') : '';
                          handleInputChange({ target: { name: 'end_time', value: formatted } });

                          if (formatted) {
                            setErrors((prev) => {
                              const updated = { ...prev };
                              delete updated.end_time;
                              return updated;
                            });
                          }
                        }}
                        ampm={false}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            size="small"
                            error={!!errors.end_time}
                            helperText={errors.end_time}
                            disabled={!values.pickup}
                            sx={enterprisePickupInputStyles}
                          />
                        )}
                      />
                    </FormControl>
                  </LocalizationProvider>
                </Box>
              </Grid>

              {/* Action Confirmation Elements Lower Control Footer Bar */}
              <Grid item xs={12}>
                <Box 
                  className="sc--form--center" 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'flex-end', 
                    pt: 2, 
                    borderTop: '1px solid #E2E8F0',
                    mt: 1
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    sx={{
                      textTransform: 'none',
                      borderRadius: '10px',
                      fontWeight: 700,
                      fontSize: '13.5px',
                      height: '38px',
                      px: 4,
                      boxShadow: 'none',
                      backgroundColor: '#1976d2',
                      color: '#FFFFFF',
                      '&:hover': { backgroundColor: '#115293', boxShadow: 'none' }
                    }}
                  >
                    Save Settings
                  </Button>
                </Box>
              </Grid>

            </Grid>
          </CardContent>
        )}
      </Card>
    </Box>
  );
};

export default PickupSettingsPage;